import { AppError } from "../utils/errorHandler";
import { APP_CONFIG } from "../constants";

const API_URL = APP_CONFIG.API_URL;

interface ErrorResponse {
  message: string | string[];
  statusCode?: number;
  error?: string;
}

class ApiClient {
  private getHeaders(): HeadersInit {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData: ErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new AppError(response.statusText, response.status);
      }

      const message = Array.isArray(errorData.message)
        ? errorData.message[0]
        : errorData.message || response.statusText;

      throw new AppError(message, response.status);
    }

    const text = await response.text();
    if (!text.trim()) {
      return null as T;
    }
    return JSON.parse(text) as T;
  }

  async get<T>(
    endpoint: string,
    options?: { cache?: RequestCache }
  ): Promise<T> {
    try {
      const noStore = options?.cache === "no-store";
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          ...this.getHeaders(),
          ...(noStore
            ? {
                "Cache-Control": "no-store, no-cache, must-revalidate",
                Pragma: "no-cache",
              }
            : {}),
        },
        cache: options?.cache ?? "default",
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw new AppError("Erro de conexão", 0);
    }
  }

  async post<T>(
    endpoint: string,
    data: unknown,
    options?: { timeoutMs?: number }
  ): Promise<T> {
    try {
      const signal =
        options?.timeoutMs != null
          ? AbortSignal.timeout(options.timeoutMs)
          : undefined;
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify(data),
        signal,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof AppError) throw error;
      if (error instanceof Error && error.name === "TimeoutError") {
        throw new AppError(
          "A operação demorou demais. Verifique sua conexão e tente de novo.",
          0
        );
      }
      throw new AppError("Erro de conexão", 0);
    }
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw new AppError("Erro de conexão", 0);
    }
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "PATCH",
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw new AppError("Erro de conexão", 0);
    }
  }

  async delete(endpoint: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: "DELETE",
        headers: this.getHeaders(),
      });
      await this.handleResponse(response);
    } catch (error) {
      if (error instanceof AppError) throw error;
      throw new AppError("Erro de conexão", 0);
    }
  }
}

export const apiClient = new ApiClient();
